import React, { createContext, useContext, useMemo } from 'react';
import useSectionsProgressData from 'hooks/sectionsProgress/useSectionsProgressData';
import useSectionsProgressManager from 'hooks/sectionsProgress/useSectionsProgressManager';
import { getStepURLs } from 'hooks/sectionsProgress/utils';
import useBaseURL from 'hooks/useBaseURL';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
const SectionsProgressContext = createContext(null);

export const useSectionsProgressContext = (sectionName) => {
  const baseURL = useBaseURL();
  const { isPrimaryUser, isUnInvitedSecondaryUser } =
    useHouseholdMembersContext();

  const context = useContext(SectionsProgressContext);
  if (!context) {
    throw new Error(
      'useSectionsProgress must be used within a SectionsProgressProvider',
    );
  }

  return useMemo(() => {
    let newContext = context;

    if (sectionName) {
      const currentSectionProgress =
        newContext.sectionsProgress[sectionName] ?? {};
      newContext = {
        ...newContext,
        currentSectionProgress,
        ...getStepURLs({
          steps: currentSectionProgress.steps,
          sectionName,
          baseURL,
          isPrimaryUser,
          isUnInvitedSecondaryUser,
        }),
      };
    }

    return newContext;
  }, [context, sectionName, baseURL, isPrimaryUser, isUnInvitedSecondaryUser]);
};

export const SectionsProgressProvider = ({ children }) => {
  const {
    deleteSectionsProgress,
    setSectionsProgress,
    sectionsProgress,
    isInitialized,
  } = useSectionsProgressData();

  const {
    currentSectionProgress,
    handleCompleteStep,
    handleUpdate,
    initializeSection,
    updateSteps,
    currentURL,
    previousURL,
    nextURL,
  } = useSectionsProgressManager({
    deleteSectionsProgress,
    setSectionsProgress,
    sectionsProgress,
    isInitialized,
  });

  return (
    <SectionsProgressContext.Provider
      value={{
        isInitialized,
        deleteSectionsProgress,
        setSectionsProgress,
        sectionsProgress,
        currentSectionProgress,
        handleCompleteStep,
        handleUpdate,
        initializeSection,
        updateSteps,
        currentURL,
        previousURL,
        nextURL,
      }}
    >
      {children}
    </SectionsProgressContext.Provider>
  );
};
